import { z } from 'zod';
import { ADMIN_ROLES, APPROVAL_STATUSES, LEAD_TYPES, PLATFORMS } from '../enums.js';
import { passwordSchema } from './auth.js';
import {
  emailSchema,
  optionalShortText,
  paginationSchema,
  phoneSchema,
  searchSchema,
  shortText,
  uuid,
} from './common.js';

export const createAdminUserSchema = z.object({
  name: shortText(120),
  email: emailSchema,
  username: shortText(64).regex(/^[a-zA-Z0-9._-]+$/, 'Nur Buchstaben, Ziffern . _ -'),
  phone: phoneSchema.optional(),
  role: z.enum(ADMIN_ROLES).default('ADMIN'),
  password: passwordSchema,
});

export const updateAdminUserSchema = createAdminUserSchema
  .partial()
  .omit({ password: true })
  .extend({ isActive: z.boolean().optional() });

/** Admin decision on a partner registration (legacy acceptApplicant.php). */
export const partnerDecisionSchema = z
  .object({
    decision: z.enum(['APPROVED', 'DECLINED']),
    comment: optionalShortText(1000),
    /** PDFs the admin attaches to an approval, uploaded beforehand. */
    documentAssetIds: z.array(uuid).max(8).default([]),
  })
  .superRefine((value, ctx) => {
    if (value.decision === 'DECLINED' && !value.comment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'Begründung bei Ablehnung erforderlich',
      });
    }
  });

export const partnerListQuerySchema = paginationSchema.extend({
  status: z.enum(APPROVAL_STATUSES).optional(),
  search: searchSchema,
});

export const userListQuerySchema = paginationSchema.extend({
  search: searchSchema,
  hasSubmittedLead: z.coerce.boolean().optional(),
});

export const leadListAdminQuerySchema = paginationSchema.extend({
  type: z.enum(LEAD_TYPES).optional(),
  search: searchSchema,
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

export const appReleaseSchema = z.object({
  platform: z.enum(PLATFORMS),
  version: shortText(20).regex(/^\d+\.\d+\.\d+(\+\d+)?$/, 'Format: 1.2.3'),
  minimumVersion: shortText(20).regex(/^\d+\.\d+\.\d+(\+\d+)?$/, 'Format: 1.2.3'),
  updateAvailable: z.boolean().default(false),
  forceUpdate: z.boolean().default(false),
  isActive: z.boolean().default(true),
  releaseNotes: optionalShortText(2000),
  storeUrl: z.string().url().max(500).optional(),
});

export type PartnerDecisionInput = z.infer<typeof partnerDecisionSchema>;
export type AppReleaseInput = z.infer<typeof appReleaseSchema>;
